import { clsx } from 'clsx'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileUp,
  FolderOpen,
  Info,
  Keyboard,
  LayoutGrid,
  Monitor,
  Moon,
  MoreVertical,
  PanelLeft,
  Printer,
  Redo2,
  Save,
  Scissors,
  Search,
  Settings,
  Share2,
  Sun,
  SunMoon,
  Undo2,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  closeDocument,
  downloadCopy,
  extractPages,
  mergeFiles,
  openFromPicker,
  printDocument,
  save,
  saveAs,
  shareDocument,
} from '@/lib/commands'
import { canSaveInPlace } from '@/lib/files'
import { useDoc } from '@/store/documentStore'
import { useUi } from '@/store/uiStore'
import { Button, Divider, IconButton, Kbd } from '../ui/Button'
import { MenuItem, MenuLabel, Popover } from '../ui/Popover'

const mod = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+'

export function Logo({ className }: { className?: string }) {
  return <img src="/icons/icon.svg" alt="" className={clsx('size-7 rounded-lg', className)} />
}

function FileName() {
  const name = useDoc((s) => s.fileName)
  const dirty = useDoc((s) => s.dirty)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(name)
  useEffect(() => setValue(name), [name])
  if (editing)
    return (
      <input
        autoFocus
        value={value}
        aria-label="File name"
        onChange={(e) => setValue(e.target.value)}
        onFocus={(e) => e.target.setSelectionRange(0, e.target.value.replace(/\.pdf$/i, '').length)}
        onBlur={() => {
          setEditing(false)
          if (value.trim() && value !== name) useDoc.getState().rename(value.trim())
        }}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') {
            setValue(name)
            setEditing(false)
          }
        }}
        className="h-8 w-full min-w-0 max-w-72 rounded-lg border border-accent bg-surface px-2 font-medium text-sm outline-none"
      />
    )
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Rename"
      className="flex h-8 min-w-0 max-w-full items-center gap-2 rounded-lg px-2 font-medium text-sm hover:bg-surface-3 md:max-w-72"
    >
      <span className="truncate">{name.replace(/\.pdf$/i, '')}</span>
      {dirty && (
        <span
          className="size-1.5 shrink-0 rounded-full bg-accent"
          title="Unsaved changes (autosaved on this device)"
        />
      )}
    </button>
  )
}

export function PageNav() {
  const current = useUi((s) => s.currentPage)
  const total = useDoc((s) => s.pages.length)
  const [value, setValue] = useState(String(current + 1))
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setValue(String(current + 1))
  }, [current])
  const go = (i: number) => useUi.getState().goToPage(Math.max(0, Math.min(total - 1, i)))
  return (
    <div className="flex items-center gap-0.5">
      <IconButton label="Previous page" size="sm" disabled={current <= 0} onClick={() => go(current - 1)}>
        <ChevronLeft className="size-4" />
      </IconButton>
      <form
        className="flex items-center gap-1 text-sm"
        onSubmit={(e) => {
          e.preventDefault()
          const n = Number.parseInt(value, 10)
          if (!Number.isNaN(n)) go(n - 1)
          inputRef.current?.blur()
        }}
      >
        <input
          ref={inputRef}
          aria-label="Page number"
          inputMode="numeric"
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
          onFocus={(e) => e.target.select()}
          onBlur={() => setValue(String(current + 1))}
          onKeyDown={(e) => e.stopPropagation()}
          className="h-7 w-10 rounded-lg border border-line bg-surface-2 text-center tabular-nums outline-none focus:border-accent"
        />
        <span className="whitespace-nowrap text-fg-3 tabular-nums">/ {total}</span>
      </form>
      <IconButton label="Next page" size="sm" disabled={current >= total - 1} onClick={() => go(current + 1)}>
        <ChevronRight className="size-4" />
      </IconButton>
    </div>
  )
}

const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5, 6, 8]
export const zoomStep = (z: number, dir: 1 | -1) =>
  dir > 0
    ? (ZOOM_STEPS.find((s) => s > z + 0.001) ?? 8)
    : ([...ZOOM_STEPS].reverse().find((s) => s < z - 0.001) ?? 0.25)

export function ZoomControl() {
  const zoom = useUi((s) => s.zoom)
  const fit = useUi((s) => s.fit)
  const set = useUi((s) => s.setZoom)
  return (
    <div className="flex items-center gap-0.5">
      <IconButton label="Zoom out" shortcut={`${mod}−`} size="sm" onClick={() => set(zoomStep(zoom, -1))}>
        <ZoomOut className="size-4" />
      </IconButton>
      <Popover
        align="center"
        trigger={({ toggle, ref }) => (
          <button
            ref={ref}
            type="button"
            onClick={toggle}
            aria-label="Zoom level"
            className="flex h-8 w-[70px] items-center justify-center gap-0.5 rounded-lg text-sm tabular-nums hover:bg-surface-3"
          >
            {Math.round(zoom * 100)}%<ChevronDown className="size-3 text-fg-3" />
          </button>
        )}
      >
        {(close) => (
          <div className="w-44">
            <MenuItem
              label="Fit width"
              hint={fit === 'width' ? '✓' : undefined}
              onSelect={() => {
                useUi.getState().setFit('width')
                close()
              }}
            />
            <MenuItem
              label="Fit page"
              hint={fit === 'page' ? '✓' : `${mod}0`}
              onSelect={() => {
                useUi.getState().setFit('page')
                close()
              }}
            />
            <div className="my-1 h-px bg-line" />
            {[0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4].map((z) => (
              <MenuItem
                key={z}
                label={`${z * 100}%`}
                hint={z === 1 ? `${mod}1` : undefined}
                onSelect={() => {
                  set(z)
                  close()
                }}
              />
            ))}
          </div>
        )}
      </Popover>
      <IconButton label="Zoom in" shortcut={`${mod}+`} size="sm" onClick={() => set(zoomStep(zoom, 1))}>
        <ZoomIn className="size-4" />
      </IconButton>
    </div>
  )
}

function ThemeMenu() {
  const theme = useUi((s) => s.theme)
  const night = useUi((s) => s.nightMode)
  return (
    <>
      <MenuLabel>Appearance</MenuLabel>
      <div className="grid grid-cols-3 gap-1 px-1.5 pb-1">
        {(
          [
            ['light', Sun, 'Light'],
            ['dark', Moon, 'Dark'],
            ['system', Monitor, 'Auto'],
          ] as const
        ).map(([id, Icon, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => useUi.getState().setTheme(id)}
            className={clsx(
              'flex flex-col items-center gap-1 rounded-xl border py-2 text-xs',
              theme === id ? 'border-accent bg-accent-soft text-accent' : 'border-line hover:bg-surface-3',
            )}
          >
            <Icon className="size-4" />
            {label}
          </button>
        ))}
      </div>
      <MenuItem
        icon={<SunMoon />}
        label="Night reading"
        hint={night ? 'On' : 'Off'}
        onSelect={() => useUi.getState().toggleNight()}
      />
    </>
  )
}

export function MainMenu({ compact }: { compact?: boolean }) {
  return (
    <Popover
      align="end"
      trigger={({ toggle, ref }) => (
        <IconButton ref={ref} label="Menu" onClick={toggle}>
          <MoreVertical className="size-[18px]" />
        </IconButton>
      )}
    >
      {(close) => {
        const run = (fn: () => unknown) => () => {
          close()
          void fn()
        }
        return (
          <div className="max-h-[80dvh] w-64 overflow-y-auto">
            {compact && (
              <>
                <div className="flex items-center justify-between px-1.5 py-1">
                  <ZoomControl />
                </div>
                <div className="my-1 h-px bg-line" />
              </>
            )}
            <MenuItem icon={<FolderOpen />} label="Open…" hint={`${mod}O`} onSelect={run(openFromPicker)} />
            {canSaveInPlace && (
              <MenuItem icon={<Save />} label="Save as…" hint={`${mod}⇧S`} onSelect={run(saveAs)} />
            )}
            <MenuItem icon={<Download />} label="Download a copy" onSelect={run(downloadCopy)} />
            <MenuItem icon={<Share2 />} label="Share…" onSelect={run(shareDocument)} />
            <MenuItem icon={<Printer />} label="Print…" hint={`${mod}P`} onSelect={run(printDocument)} />
            <div className="my-1 h-px bg-line" />
            <MenuItem icon={<FileUp />} label="Combine with other PDFs…" onSelect={run(() => mergeFiles())} />
            <MenuItem
              icon={<LayoutGrid />}
              label="Organize pages"
              onSelect={run(() => useUi.getState().setOrganize(true))}
            />
            <MenuItem
              icon={<Scissors />}
              label="Extract this page…"
              onSelect={run(() => {
                const { pages } = useDoc.getState()
                const p = pages[useUi.getState().currentPage]
                return p && extractPages([p.id])
              })}
            />
            <div className="my-1 h-px bg-line" />
            <ThemeMenu />
            <div className="my-1 h-px bg-line" />
            <MenuItem
              icon={<Settings />}
              label="Settings"
              onSelect={run(() => useUi.getState().openDialog('settings'))}
            />
            <MenuItem
              icon={<Keyboard />}
              label="Keyboard shortcuts"
              hint="?"
              onSelect={run(() => useUi.getState().openDialog('shortcuts'))}
            />
            <MenuItem
              icon={<Info />}
              label="About Folio"
              onSelect={run(() => useUi.getState().openDialog('about'))}
            />
            <div className="my-1 h-px bg-line" />
            <MenuItem icon={<X />} label="Close document" onSelect={run(closeDocument)} />
          </div>
        )
      }}
    </Popover>
  )
}

export function TopBar({ compact }: { compact: boolean }) {
  const canUndo = useDoc((s) => s.past.length > 0)
  const canRedo = useDoc((s) => s.future.length > 0)
  const searchOpen = useUi((s) => s.searchOpen)
  const organize = useUi((s) => s.organizeMode)
  const sidebarOpen = useUi((s) => s.sidebarOpen)

  return (
    <header className="titlebar-drag safe-top relative z-40 shrink-0 border-line border-b bg-surface">
      <div className="flex h-12 items-center gap-1 px-2">
        <button
          type="button"
          aria-label="Home"
          onClick={() => void closeDocument()}
          className="flex size-9 shrink-0 items-center justify-center rounded-xl hover:bg-surface-3"
        >
          <Logo className="size-6" />
        </button>
        {!organize && (
          <IconButton
            label="Toggle sidebar"
            active={sidebarOpen}
            onClick={() => useUi.getState().toggleSidebar()}
          >
            <PanelLeft className="size-[18px]" />
          </IconButton>
        )}
        <div className="min-w-0 flex-1 md:flex-none">
          <FileName />
        </div>

        {!compact && (
          <div className="mx-auto flex items-center gap-2">
            <PageNav />
            <Divider />
            <ZoomControl />
          </div>
        )}

        <div className={clsx('flex shrink-0 items-center gap-0.5', compact && 'ml-auto')}>
          <IconButton
            label="Undo"
            shortcut={`${mod}Z`}
            disabled={!canUndo}
            onClick={() => useDoc.getState().undo()}
          >
            <Undo2 className="size-[18px]" />
          </IconButton>
          <IconButton
            label="Redo"
            shortcut={`${mod}⇧Z`}
            disabled={!canRedo}
            onClick={() => useDoc.getState().redo()}
          >
            <Redo2 className="size-[18px]" />
          </IconButton>
          {!compact && <Divider />}
          <IconButton
            label="Search"
            shortcut={`${mod}F`}
            active={searchOpen}
            onClick={() => useUi.getState().setSearchOpen(!searchOpen)}
          >
            <Search className="size-[18px]" />
          </IconButton>
          {!compact && (
            <IconButton
              label="Organize pages"
              active={organize}
              onClick={() => useUi.getState().setOrganize(!organize)}
            >
              <LayoutGrid className="size-[18px]" />
            </IconButton>
          )}
          <MainMenu compact={compact} />
          <Button
            variant="primary"
            size="sm"
            className="ml-1"
            icon={<Save className="size-4" />}
            onClick={() => void save()}
            title={`Save  ${mod}S`}
          >
            <span className="max-sm:hidden">Save</span>
          </Button>
        </div>
      </div>
    </header>
  )
}

export { Kbd, mod }
